import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

export default function Q360ErrorState({
  message = "Une erreur est survenue.",
  title,
  description,
  compact = false,
  icon = "!",
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className,
}) {
  const hasStructuredCopy = Boolean(title || description);
  const hasPrimaryAction = Boolean(actionLabel && onAction);
  const hasSecondaryAction = Boolean(
    secondaryActionLabel && onSecondaryAction
  );
  const hasActions = hasPrimaryAction || hasSecondaryAction;

  return (
    <ErrorState
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      $compact={compact}
      className={className}
      data-density={compact ? "compact" : "default"}
    >
      <ErrorIcon aria-hidden="true" $compact={compact}>
        {icon}
      </ErrorIcon>

      <ErrorCopy>
        {hasStructuredCopy ? (
          <>
            {title && <ErrorTitle>{title}</ErrorTitle>}

            {description && (
              <ErrorDescription>
                {description}
              </ErrorDescription>
            )}
          </>
        ) : (
          <ErrorMessage>{message}</ErrorMessage>
        )}
      </ErrorCopy>

      {hasActions && (
        <ErrorActions>
          {hasPrimaryAction && (
            <PrimaryAction
              type="button"
              onClick={onAction}
            >
              {actionLabel}
            </PrimaryAction>
          )}

          {hasSecondaryAction && (
            <SecondaryAction
              type="button"
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </SecondaryAction>
          )}
        </ErrorActions>
      )}
    </ErrorState>
  );
}

Q360ErrorState.propTypes = {
  message: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  compact: PropTypes.bool,
  icon: PropTypes.node,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  secondaryActionLabel: PropTypes.string,
  onSecondaryAction: PropTypes.func,
  className: PropTypes.string,
};

const ErrorState = styled.section`
  min-height: ${({ $compact }) => ($compact ? "52px" : "112px")};
  padding: ${({ $compact }) =>
    $compact ? "0.7rem 0.85rem" : "1.1rem"};
  border: 1px solid rgba(255, 180, 180, 0.28);
  border-radius: ${({ $compact }) =>
    $compact
      ? "var(--q360-radius-sm, 10px)"
      : "18px"};
  background: rgba(120, 25, 36, 0.2);
  color: var(--q360-text-primary, #f7f7f7);

  display: grid;
  grid-template-columns:
    auto minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "0.7rem" : "1rem")};

  @media (max-width: 760px) {
    grid-template-columns: auto minmax(0, 1fr);

    & > :last-child:not(:nth-child(2)) {
      grid-column: 1 / -1;
    }
  }
`;

const ErrorIcon = styled.span`
  width: ${({ $compact }) => ($compact ? "32px" : "40px")};
  height: ${({ $compact }) => ($compact ? "32px" : "40px")};
  border-radius: 999px;
  background: var(--q360-text-primary, #fff9ea);
  color: #8a1f2d;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: ${({ $compact }) =>
    $compact ? "1rem" : "1.25rem"};
  font-weight: 950;
`;

const ErrorCopy = styled.div`
  min-width: 0;
`;

const ErrorMessage = styled.p`
  margin: 0;
  color: var(--q360-text-primary, #f7f7f7);
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.45;
`;

const ErrorTitle = styled.h3`
  margin: 0;
  color: var(--q360-text-primary, #f7f7f7);
  font-size: 1rem;
  font-weight: 850;
`;

const ErrorDescription = styled.p`
  margin: 0.35rem 0 0;
  color: var(
    --q360-text-muted,
    rgba(247, 247, 247, 0.76)
  );
  font-size: 0.9rem;
  line-height: 1.45;
`;

const ErrorActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const BaseAction = styled.button`
  min-height: 36px;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 850;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(
      --q360-text-primary,
      #fff9ea
    );
    outline-offset: 2px;
  }
`;

const PrimaryAction = styled(BaseAction)`
  border: none;
  background: var(--q360-text-primary, #fff9ea);
  color: var(--q360-surface-base, #0b1328);

  &:hover {
    background: #ffffff;
  }
`;

const SecondaryAction = styled(BaseAction)`
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: transparent;
  color: var(--q360-text-primary, #f7f7f7);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;
